/**
 * Provider Recommendation Engine
 *
 * Scores and ranks providers for patient intake assignments
 */

import { ProviderCapacityDay } from '../types/capacity.types';
import { IntakePatient, ProviderRecommendation } from '../types/intake.types';
import { formatDateKey } from '../utils/dateHelpers';

interface ProviderWithMetadata {
  providerId: number;
  providerName: string;
  role: string;
  territory: string;
  panelSize: number;
}

/**
 * Get provider recommendations for a specific patient
 *
 * @param patient - Patient to assign
 * @param capacitySnapshot - Current capacity data
 * @param providerMetadata - Provider territories and panel sizes
 * @param targetUtilization - Target utilization threshold (0-100)
 * @returns Ranked list of provider recommendations
 */
export function getProviderRecommendations(
  patient: IntakePatient,
  capacitySnapshot: ProviderCapacityDay[],
  providerMetadata: ProviderWithMetadata[],
  targetUtilization: number = 75
): ProviderRecommendation[] {
  const intakeDateKey = formatDateKey(patient.intakeVisitDate);

  // Find capacity for all providers on intake date
  const providersOnDate = capacitySnapshot.filter((day) => day.date === intakeDateKey);

  // Build recommendations
  const recommendations: ProviderRecommendation[] = [];

  providerMetadata.forEach((metadata) => {
    // Filter by role match
    if (metadata.role !== patient.requiredRole) {
      return;
    }

    // Filter by territory match
    if (metadata.territory !== patient.territory) {
      return;
    }

    // Find capacity for this provider on intake date
    const capacityOnDate = providersOnDate.find((day) => day.providerId === metadata.providerId);

    if (!capacityOnDate) {
      return; // Provider not scheduled on this date
    }

    const utilization = capacityOnDate.utilization;
    const availableMinutes = capacityOnDate.remainingMinutes;
    const isOverThreshold = utilization > targetUtilization;

    // Calculate recommendation score (0-100)
    const score = calculateRecommendationScore(
      utilization,
      availableMinutes,
      metadata.panelSize,
      targetUtilization,
      patient.expectedVisitsPerWeek,
      patient.expectedDurationWeeks
    );

    // Generate reason
    const reason = generateRecommendationReason(
      utilization,
      availableMinutes,
      metadata.panelSize,
      targetUtilization,
      isOverThreshold
    );

    recommendations.push({
      providerId: metadata.providerId,
      providerName: metadata.providerName,
      role: metadata.role,
      territory: metadata.territory,
      panelSize: metadata.panelSize,
      utilizationPercent: Math.round(utilization),
      availableMinutes,
      score,
      reason,
      isOverThreshold,
    });
  });

  // Sort by score (highest first), then by multiple tiebreakers
  recommendations.sort((a, b) => {
    // Primary: Score (highest first)
    if (b.score !== a.score) {
      return b.score - a.score;
    }

    // Tiebreaker 1: Available capacity (highest first)
    if (b.availableMinutes !== a.availableMinutes) {
      return b.availableMinutes - a.availableMinutes;
    }

    // Tiebreaker 2: Utilization (lowest first)
    if (a.utilizationPercent !== b.utilizationPercent) {
      return a.utilizationPercent - b.utilizationPercent;
    }

    // Tiebreaker 3: Panel size (smallest first)
    return a.panelSize - b.panelSize;
  });

  return recommendations;
}

/**
 * Calculate recommendation score (0-100)
 *
 * Higher score = better fit
 *
 * Key factors:
 * 1. Distance from utilization threshold (prefer providers with more cushion)
 * 2. Available capacity (prefer more visits available)
 * 3. Panel size (distribute load across providers)
 */
function calculateRecommendationScore(
  utilization: number,
  availableMinutes: number,
  panelSize: number,
  targetUtilization: number,
  visitsPerWeek: number,
  durationWeeks: number
): number {
  let score = 100;

  // Major penalty for being over target utilization
  if (utilization > targetUtilization) {
    const overagePercent = utilization - targetUtilization;
    score -= overagePercent * 3; // -3 points per % over target (increased from 2)
  } else {
    // Even under target, penalize being close to threshold
    const cushion = targetUtilization - utilization;
    if (cushion < 5) {
      // Very close to threshold (70-75% when target is 75%)
      score -= (5 - cushion) * 2; // Up to -10 points for being within 5% of threshold
    } else if (cushion >= 15) {
      // Good cushion (under 60% when target is 75%)
      score += 10; // Bonus for having lots of room
    }
  }

  // Score based on available capacity (visit capacity, not just minutes)
  // Standard visit = 75 minutes (60 min visit + 15 min travel)
  const standardVisitMinutes = 75;
  const visitCapacity = Math.floor(availableMinutes / standardVisitMinutes);

  if (availableMinutes < 60) {
    score -= 50; // Major penalty if can't fit intake
  } else if (visitCapacity === 0) {
    score -= 30; // Can fit intake, but no room for follow-ups
  } else if (visitCapacity === 1) {
    score -= 15; // Tight capacity (only 1 visit)
  } else if (visitCapacity === 2) {
    score -= 5; // Moderate capacity (2 visits)
  } else if (visitCapacity === 3) {
    score += 5; // Good capacity (3 visits)
  } else if (visitCapacity >= 4) {
    score += 15; // Excellent capacity (4+ visits)
  }

  // Panel size considerations (distribute workload)
  if (panelSize < 10) {
    score += 15; // Strong bonus for small panels
  } else if (panelSize < 15) {
    score += 8; // Good bonus for medium-small panels
  } else if (panelSize >= 20) {
    score -= 12; // Penalty for large panels
  } else if (panelSize >= 18) {
    score -= 6; // Moderate penalty for medium-large panels
  }

  // Ensure score is 0-100
  return Math.max(0, Math.min(100, score));
}

/**
 * Generate human-readable recommendation reason
 */
function generateRecommendationReason(
  utilization: number,
  availableMinutes: number,
  panelSize: number,
  targetUtilization: number,
  isOverThreshold: boolean
): string {
  if (isOverThreshold) {
    return `Over target utilization (${Math.round(utilization)}% > ${targetUtilization}%)`;
  }

  if (availableMinutes < 60) {
    return 'Limited capacity on intake date';
  }

  if (utilization < 50) {
    return `Good capacity, low utilization (${Math.round(utilization)}%)`;
  }

  if (utilization >= 50 && utilization <= 75) {
    return `Balanced workload (${Math.round(utilization)}% utilization)`;
  }

  if (panelSize < 10) {
    return `Small panel (${panelSize} patients), good capacity`;
  }

  return `Available capacity: ${availableMinutes} min on intake date`;
}

/**
 * Filter out recommendations that don't meet minimum criteria
 */
export function filterViableRecommendations(
  recommendations: ProviderRecommendation[],
  includeOverThreshold: boolean = false
): ProviderRecommendation[] {
  return recommendations.filter((rec) => {
    // Must have enough capacity for 60min intake
    if (rec.availableMinutes < 60) {
      return false;
    }

    // Filter out over-threshold unless explicitly included
    if (!includeOverThreshold && rec.isOverThreshold) {
      return false;
    }

    return true;
  });
}

/**
 * Find alternative dates with available capacity
 *
 * Searches ±1, ±2, ±3 days from preferred date
 */
export function findAlternativeDates(
  patient: IntakePatient,
  capacitySnapshot: ProviderCapacityDay[],
  providerMetadata: ProviderWithMetadata[],
  targetUtilization: number = 75,
  maxDaysOffset: number = 3
): Array<{ date: Date; daysOffset: number; availableProvidersCount: number; bestProviderName?: string; bestProviderScore?: number }> {
  const alternatives: Array<{
    date: Date;
    daysOffset: number;
    availableProvidersCount: number;
    bestProviderName?: string;
    bestProviderScore?: number
  }> = [];

  // Try dates around the preferred intake date
  for (let offset = 1; offset <= maxDaysOffset; offset++) {
    // Try positive offset (later dates)
    const laterDate = new Date(patient.intakeVisitDate);
    laterDate.setDate(laterDate.getDate() + offset);

    const laterPatient = { ...patient, intakeVisitDate: laterDate };
    const laterRecs = getProviderRecommendations(laterPatient, capacitySnapshot, providerMetadata, targetUtilization);
    const laterViable = filterViableRecommendations(laterRecs, false);

    if (laterViable.length > 0) {
      const best = laterViable[0];
      alternatives.push({
        date: laterDate,
        daysOffset: offset,
        availableProvidersCount: laterViable.length,
        bestProviderName: best.providerName,
        bestProviderScore: best.score,
      });
    }

    // Try negative offset (earlier dates)
    const earlierDate = new Date(patient.intakeVisitDate);
    earlierDate.setDate(earlierDate.getDate() - offset);

    const earlierPatient = { ...patient, intakeVisitDate: earlierDate };
    const earlierRecs = getProviderRecommendations(earlierPatient, capacitySnapshot, providerMetadata, targetUtilization);
    const earlierViable = filterViableRecommendations(earlierRecs, false);

    if (earlierViable.length > 0) {
      const best = earlierViable[0];
      alternatives.push({
        date: earlierDate,
        daysOffset: -offset,
        availableProvidersCount: earlierViable.length,
        bestProviderName: best.providerName,
        bestProviderScore: best.score,
      });
    }
  }

  // Sort by best score, then by closest date
  alternatives.sort((a, b) => {
    const scoreDiff = (b.bestProviderScore || 0) - (a.bestProviderScore || 0);
    if (scoreDiff !== 0) return scoreDiff;
    return Math.abs(a.daysOffset) - Math.abs(b.daysOffset);
  });

  return alternatives;
}

/**
 * Find next best providers (even if over threshold or different territory)
 *
 * Returns providers that don't meet normal criteria but could be fallback options
 */
export function findNextBestProviders(
  patient: IntakePatient,
  capacitySnapshot: ProviderCapacityDay[],
  providerMetadata: ProviderWithMetadata[],
  targetUtilization: number = 75
): Array<{ providerId: number; providerName: string; territory: string; utilizationPercent: number; availableMinutes: number; score: number; reason: string; warning: string }> {
  const intakeDateKey = formatDateKey(patient.intakeVisitDate);
  const providersOnDate = capacitySnapshot.filter((day) => day.date === intakeDateKey);

  const nextBestOptions: Array<{
    providerId: number;
    providerName: string;
    territory: string;
    utilizationPercent: number;
    availableMinutes: number;
    score: number;
    reason: string;
    warning: string;
  }> = [];

  providerMetadata.forEach((metadata) => {
    // Only match role
    if (metadata.role !== patient.requiredRole) {
      return;
    }

    const capacityOnDate = providersOnDate.find((day) => day.providerId === metadata.providerId);
    if (!capacityOnDate) {
      return;
    }

    const utilization = capacityOnDate.utilization;
    const availableMinutes = capacityOnDate.remainingMinutes;

    // Must have at least 60 minutes for intake
    if (availableMinutes < 60) {
      return;
    }

    // Calculate score
    const score = calculateRecommendationScore(
      utilization,
      availableMinutes,
      metadata.panelSize,
      targetUtilization,
      patient.expectedVisitsPerWeek,
      patient.expectedDurationWeeks
    );

    // Determine warning
    const warnings: string[] = [];
    const isOverThreshold = utilization > targetUtilization;
    const isDifferentTerritory = metadata.territory !== patient.territory;

    if (isOverThreshold) {
      warnings.push(`Over ${targetUtilization}% utilization (${Math.round(utilization)}%)`);
    }
    if (isDifferentTerritory) {
      warnings.push(`Different territory (${metadata.territory} vs ${patient.territory})`);
    }
    if (metadata.panelSize > 20) {
      warnings.push(`Large panel size (${metadata.panelSize} patients)`);
    }

    // Only include if there's at least one warning (otherwise it would be in normal recommendations)
    if (warnings.length > 0) {
      nextBestOptions.push({
        providerId: metadata.providerId,
        providerName: metadata.providerName,
        territory: metadata.territory,
        utilizationPercent: Math.round(utilization),
        availableMinutes,
        score,
        reason: generateRecommendationReason(utilization, availableMinutes, metadata.panelSize, targetUtilization, isOverThreshold),
        warning: warnings.join('; '),
      });
    }
  });

  // Sort by score
  nextBestOptions.sort((a, b) => b.score - a.score);

  return nextBestOptions.slice(0, 3); // Return top 3 next best options
}

/**
 * Generate provisional assignment option
 */
export function generateProvisionalOption(
  patient: IntakePatient
): { intakeVisitDate: Date; suggestedAction: string; description: string } {
  return {
    intakeVisitDate: patient.intakeVisitDate,
    suggestedAction: 'Assign to Marketplace',
    description: `Create provisional shift on ${patient.intakeVisitDate.toLocaleDateString()} that can be picked up by available providers. This allows flexibility for providers to claim the visit as their schedule allows.`,
  };
}
